import {
    Box,
    Heading,
    Text,
    Card,
    CardBody,
    Flex,
    Badge,
    Grid,
    Stat,
    StatLabel,
    StatNumber,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    Spinner,
    Center,
    Button,
    HStack,
    Icon,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
} from '@chakra-ui/react';
import { useParams, Link } from 'react-router-dom';
import { MdChevronRight, MdDescription, MdDownload, MdAdd } from 'react-icons/md';

import { useProject, useProjectBidItems } from '@/services/hooks';

export function ProjectDetail() {
    const { id } = useParams();
    const { data: project, isLoading, error } = useProject(Number(id));
    const { data: bidItemsData, isLoading: bidItemsLoading } = useProjectBidItems(Number(id));

    const formatDate = (date: string) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    const formatCurrency = (amount: number | null) => {
        if (!amount) return '-';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    };

    if (isLoading) {
        return (
            <Center h="50vh">
                <Spinner size="xl" color="brand.500" />
            </Center>
        );
    }

    if (error || !project) {
        return (
            <Center h="50vh">
                <Box textAlign="center">
                    <Text color="red.400" mb={4}>Failed to load project</Text>
                    <Button as={Link} to="/projects">Back to Projects</Button>
                </Box>
            </Center>
        );
    }

    const bidItems = bidItemsData?.bid_items || [];

    return (
        <Box>
            {/* Breadcrumb */}
            <Breadcrumb separator={<Icon as={MdChevronRight} color="gray.500" />} mb={6}>
                <BreadcrumbItem>
                    <BreadcrumbLink as={Link} to="/projects" color="gray.400">
                        Projects
                    </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbItem isCurrentPage>
                    <BreadcrumbLink color="white">{project.title}</BreadcrumbLink>
                </BreadcrumbItem>
            </Breadcrumb>

            {/* Header */}
            <Flex justify="space-between" align="flex-start" mb={6}>
                <Box>
                    <Heading size="lg" mb={2}>{project.title}</Heading>
                    <HStack spacing={3}>
                        <Badge colorScheme="brand">{project.agency}</Badge>
                        <Badge colorScheme={project.status === 'active' ? 'green' : 'gray'}>
                            {project.status}
                        </Badge>
                        {project.contract_id && (
                            <Text fontSize="sm" color="gray.400">
                                Contract: {project.contract_id}
                            </Text>
                        )}
                    </HStack>
                </Box>
                <HStack>
                    <Button leftIcon={<MdDownload />} variant="outline" size="sm">
                        Export
                    </Button>
                    <Button leftIcon={<MdAdd />} colorScheme="brand" size="sm">
                        Add Bid Items
                    </Button>
                </HStack>
            </Flex>

            {/* Stats */}
            <Grid templateColumns={{ base: '1fr', md: 'repeat(4, 1fr)' }} gap={4} mb={6}>
                <Card>
                    <CardBody>
                        <Stat>
                            <StatLabel color="gray.400">Award Amount</StatLabel>
                            <StatNumber fontSize="xl">{formatCurrency(project.award_amount)}</StatNumber>
                        </Stat>
                    </CardBody>
                </Card>
                <Card>
                    <CardBody>
                        <Stat>
                            <StatLabel color="gray.400">Award Date</StatLabel>
                            <StatNumber fontSize="xl">{formatDate(project.award_date)}</StatNumber>
                        </Stat>
                    </CardBody>
                </Card>
                <Card>
                    <CardBody>
                        <Stat>
                            <StatLabel color="gray.400">Contractor</StatLabel>
                            <StatNumber fontSize="xl">{project.contractor || '-'}</StatNumber>
                        </Stat>
                    </CardBody>
                </Card>
                <Card>
                    <CardBody>
                        <Stat>
                            <StatLabel color="gray.400">Bid Items</StatLabel>
                            <StatNumber fontSize="xl">{bidItems.length}</StatNumber>
                        </Stat>
                    </CardBody>
                </Card>
            </Grid>

            {/* Tabs */}
            <Tabs colorScheme="brand" variant="enclosed">
                <TabList>
                    <Tab>Bid Items</Tab>
                    <Tab>Documents</Tab>
                    <Tab>Activity</Tab>
                </TabList>

                <TabPanels>
                    {/* Bid Items Tab */}
                    <TabPanel px={0}>
                        <Card>
                            <CardBody p={0}>
                                <Table variant="simple">
                                    <Thead>
                                        <Tr>
                                            <Th>Item Code</Th>
                                            <Th>Description</Th>
                                            <Th>UOM</Th>
                                            <Th isNumeric>Quantity</Th>
                                            <Th isNumeric>Unit Price</Th>
                                            <Th isNumeric>Extended</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {bidItemsLoading ? (
                                            <Tr>
                                                <Td colSpan={6}>
                                                    <Center py={8}><Spinner /></Center>
                                                </Td>
                                            </Tr>
                                        ) : bidItems.length === 0 ? (
                                            <Tr>
                                                <Td colSpan={6}>
                                                    <Center py={8}>
                                                        <Text color="gray.500">No bid items found</Text>
                                                    </Center>
                                                </Td>
                                            </Tr>
                                        ) : (
                                            bidItems.map((item: any) => (
                                                <Tr key={item.id} _hover={{ bg: 'navy.700' }}>
                                                    <Td>
                                                        <Text fontFamily="mono" fontSize="sm">{item.item_code || '-'}</Text>
                                                    </Td>
                                                    <Td>
                                                        <Text noOfLines={1}>{item.description}</Text>
                                                    </Td>
                                                    <Td>
                                                        <Badge colorScheme="brand">{item.uom}</Badge>
                                                    </Td>
                                                    <Td isNumeric>
                                                        <Text fontFamily="mono">{item.quantity?.toLocaleString() || '-'}</Text>
                                                    </Td>
                                                    <Td isNumeric>
                                                        <Text fontFamily="mono">${item.unit_price?.toFixed(2)}</Text>
                                                    </Td>
                                                    <Td isNumeric>
                                                        <Text fontWeight="medium">
                                                            ${((item.quantity || 0) * (item.unit_price || 0)).toLocaleString()}
                                                        </Text>
                                                    </Td>
                                                </Tr>
                                            ))
                                        )}
                                    </Tbody>
                                </Table>
                            </CardBody>
                        </Card>
                    </TabPanel>

                    {/* Documents Tab */}
                    <TabPanel px={0}>
                        <Card>
                            <CardBody>
                                <Center py={8}>
                                    <Box textAlign="center">
                                        <Icon as={MdDescription} boxSize={12} color="gray.500" mb={4} />
                                        <Text color="gray.500" mb={4}>No documents attached</Text>
                                        <Button leftIcon={<MdAdd />}>Upload Document</Button>
                                    </Box>
                                </Center>
                            </CardBody>
                        </Card>
                    </TabPanel>

                    {/* Activity Tab */}
                    <TabPanel px={0}>
                        <Card>
                            <CardBody>
                                <Text color="gray.500">Activity log coming soon...</Text>
                            </CardBody>
                        </Card>
                    </TabPanel>
                </TabPanels>
            </Tabs>
        </Box>
    );
}
